/**
 * pilotOversightModel — pure rules for the Risk panel's Pilot oversight (Bill 2026-11-04). Tested in
 * __tests__/pilotOversightModel.test.ts. Same words as ROP-teams' Pilot testing (apps/ROP-teams/src/modules/Pilot/
 * pilotModel.ts); the two apps cannot share a module, so the few rules both need are repeated here.
 */
import type { StatusTone } from '@retail-ops/panel-shell';
import type { PilotCounts, PilotHistoryRow, PilotItem, PilotSection } from '@/services/pilotOversightApi';

const lc = (s: string | null | undefined): string => (s ?? '').trim().toLowerCase();

export function statusTone(status: string | null | undefined): StatusTone {
  switch (status) {
    case 'Complete': return 'ok';
    case 'In Progress': return 'open';
    case 'Issue Identified': return 'problem';
    default: return 'neutral';
  }
}

export interface OversightFilter {
  sectionId: number | 'all';
  audience: 'all' | 'testers' | 'risk';
  status: string;
  q: string;
}

export const EMPTY_FILTER: OversightFilter = { sectionId: 'all', audience: 'all', status: '', q: '' };

export function filterItems(items: ReadonlyArray<PilotItem>, sections: ReadonlyArray<PilotSection>, f: OversightFilter): PilotItem[] {
  const q = lc(f.q);
  const audience = new Map(sections.map((s) => [s.id, s.audience]));
  return items.filter((i) => {
    if (f.sectionId !== 'all' && i.sectionId !== f.sectionId) return false;
    if (f.audience !== 'all' && audience.get(i.sectionId) !== f.audience) return false;
    if (f.status === '(none)' && i.status) return false;
    if (f.status && f.status !== '(none)' && i.status !== f.status && i.result !== f.status) return false;
    if (q) {
      const hay = [i.code, i.area, i.title, i.steps, i.group, i.notes, i.assignedTo, i.testerGroup, i.riskRole, i.evidenceRef,
        ...i.details.map((d) => d.value), ...i.issues.map((x) => x.changeKey)].map(lc).join(' \u0001 ');
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

/** The workbook's Summary "Total" row across the given checklist sections. */
export function totals(sections: ReadonlyArray<PilotSection>): PilotCounts {
  const z: PilotCounts = { notStarted: 0, inProgress: 0, complete: 0, issueIdentified: 0, notApplicable: 0, total: 0, percentComplete: 0, pass: 0, fail: 0 };
  for (const s of sections) {
    if (s.kind !== 'checklist' || s.retired) continue;
    z.notStarted += s.counts.notStarted;
    z.inProgress += s.counts.inProgress;
    z.complete += s.counts.complete;
    z.issueIdentified += s.counts.issueIdentified;
    z.notApplicable += s.counts.notApplicable;
    z.total += s.counts.total;
  }
  const denom = z.total - z.notApplicable;
  z.percentComplete = denom <= 0 ? 0 : Math.round((1000 * z.complete) / denom) / 10;
  return z;
}

export function headline(item: PilotItem, section: Pick<PilotSection, 'kind'> | undefined): string {
  if (item.title?.trim()) return item.title.trim();
  if (section?.kind === 'scenarios' || section?.kind === 'results') {
    const pick = (label: string): string | null => item.details.find((d) => d.label === label)?.value?.trim() || null;
    const parts = section.kind === 'scenarios'
      ? [pick('Single or Joint'), pick('Customer Type'), item.accountTypes?.trim() || null]
      : [pick('Error Details'), pick('Change Request')];
    const line = parts.filter(Boolean).join(' · ').replace(/\s*\n\s*/g, ' / ');
    if (line) return line.length > 160 ? `${line.slice(0, 157)}…` : line;
  }
  return item.steps?.trim().split('\n')[0]?.slice(0, 160) || 'No description';
}

const ACTIONS: Record<string, string> = {
  loaded: 'Loaded from the workbook',
  added: 'Added',
  retired: 'Retired',
  restored: 'Restored',
  'issue-reported': 'Issue reported with the life ring',
  'section-added': 'Section added',
  'section-changed': 'Section changed',
  'section-retired': 'Section retired',
  'section-restored': 'Section restored',
};

const clip = (s: string): string => {
  const one = s.replace(/\s*\n\s*/g, ' / ');
  return one.length > 140 ? `${one.slice(0, 137)}…` : one;
};

/** "Status: Not Started → In Progress". */
export function historyWords(h: Pick<PilotHistoryRow, 'action' | 'field' | 'fieldLabel' | 'oldValue' | 'newValue'>): string {
  if (h.action === 'changed' || (h.action === 'section-changed' && h.field)) {
    const from = h.oldValue?.trim() ? h.oldValue.trim() : '(blank)';
    const to = h.newValue?.trim() ? h.newValue.trim() : '(blank)';
    return `${h.fieldLabel || h.field || 'Field'}: ${clip(from)} → ${clip(to)}`;
  }
  const base = ACTIONS[h.action] ?? h.action;
  return h.newValue?.trim() ? `${base}: ${clip(h.newValue.trim())}` : base;
}

export function nameOf(upn: string | null | undefined, name?: string | null): string {
  if (name?.trim() && !name.includes('@')) return name.trim();
  const u = (upn ?? '').trim();
  if (!u) return '—';
  if (u.startsWith('seed-')) return 'Workbook load';
  return (u.split('@')[0] ?? u).split(/[._-]+/).filter(Boolean).map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
}

/** Only the fields that differ from the item, ready to send. */
export function diff(item: PilotItem, draft: Record<string, string>): Record<string, string> {
  const current: Record<string, string> = {
    status: item.status ?? '', result: item.result ?? '', notes: item.notes ?? '', assignedTo: item.assignedTo ?? '',
    dueDate: item.dueDate ?? '', evidenceRef: item.evidenceRef ?? '', itemCode: item.code ?? '', groupName: item.group ?? '',
    area: item.area ?? '', title: item.title ?? '', steps: item.steps ?? '', channel: item.channel ?? '',
    testerGroup: item.testerGroup ?? '', accountTypes: item.accountTypes ?? '', riskRole: item.riskRole ?? '',
  };
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(draft)) if (k in current && current[k] !== v) out[k] = v;
  return out;
}

export function draftOf(item: PilotItem): Record<string, string> {
  return {
    status: item.status ?? '', result: item.result ?? '', notes: item.notes ?? '', assignedTo: item.assignedTo ?? '',
    dueDate: item.dueDate ?? '', evidenceRef: item.evidenceRef ?? '', itemCode: item.code ?? '', groupName: item.group ?? '',
    area: item.area ?? '', title: item.title ?? '', steps: item.steps ?? '', channel: item.channel ?? '',
    testerGroup: item.testerGroup ?? '', accountTypes: item.accountTypes ?? '', riskRole: item.riskRole ?? '',
  };
}

export function when(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}
